/**
 * Listing-engine filter params — shared helpers for the two engine JS modules
 * (components/card-grid-filters.js fires the events, blocks/card-grid-with-filters.js
 * glues them to the URL + the cards container). Both used to carry their own copy of
 * the serialiser; they import it from here now so the two can't drift.
 *
 * Three jobs:
 *   1. serializeFilterForm() — the form → AJAX/URL param object, INCLUDING the
 *      explicit off-value for an unchecked checkbox filter (a browser omits an
 *      unchecked box entirely, which the engine would read as "unset" = default).
 *   2. checkbox-filter helpers — read the declared default state, restore it.
 *   3. applyHashFilters() — deep-linking from `#hash` as well as `?query`
 *      ("the hash survives clean-URL redirects", designs/resources.html).
 */

//Marks an engine checkbox filter (rendered by components/card-grid-filters.blade.php).
const CHECKBOX_SELECTOR = 'input[type="checkbox"][data-fr-filter-checkbox]';

//Mirror of App\View\Components\CardGridFilters::CHECKBOX_OFF.
export const CHECKBOX_OFF = '0';

/**
 * Short deep-link aliases the designs use (`#cat=`, `#type=`, `#sort=`, `#q=`) mapped
 * onto the engine's real input names, which are taxonomy-driven and therefore
 * per-CPT (`resource-category`, `news-category`, `resource-type`, …). The full name
 * always works too — `#resource-category=policy-landscape` and
 * `?resource-category=policy-landscape` are equivalent.
 */
const HASH_ALIASES = [
	{ alias: 'sort', test: name => name === 'order_by' },
	{ alias: 'q', test: name => name === 'search' || name === 's' },
	{ alias: 'cat', test: name => /-category$/.test( name ) },
	{ alias: 'type', test: name => /-type$/.test( name ) },
];

/**
 * Unwrap a vanilla-jq set (or pass an Element through) to the <form> element.
 *
 * @param {*} _form
 * @returns {HTMLFormElement|null}
 */
const toFormEl = _form => {
	if ( ! _form ) return null;
	if ( _form instanceof Element ) return _form;
	return _form.el && _form.el[ 0 ] ? _form.el[ 0 ] : null;
};

/**
 * Serialise the filter form into the flat param object the engine passes to AJAX and
 * writes to the URL. Array values (repeated names) are comma-joined so URLs stay short.
 *
 * @param {*} _form vanilla-jq set or a <form> element.
 * @returns {Object}
 */
export const serializeFilterForm = _form => {
	const form = toFormEl( _form );
	if ( ! form ) return {};

	const data = window.formToObject( form, {
		includeEmptyValuedElements: true,
	} );

	for ( const [ key, value ] of Object.entries( data ) ) {
		if ( Array.isArray( value ) ) {
			data[ key ] = value.filter( n => n ).join( ',' ); //filter empty options just in case and comma separate
		}
	}

	//Checkbox filters report an explicit off value when unchecked, so "off" survives
	//the AJAX round trip and the URL instead of looking like an absent param.
	form.querySelectorAll( CHECKBOX_SELECTOR ).forEach( el => {
		if ( ! el.name || el.disabled ) return;
		data[ el.name ] = el.checked ? el.value : CHECKBOX_OFF;
	} );

	return data;
};

/**
 * The DEFAULT serialised value of every checkbox filter, keyed by input name (from
 * the `data-fr-default` the Blade view emits). Lets callers tell "at its default"
 * from "actively filtered" — a checked-by-default toggle must not count as a filter
 * (it would otherwise hide the pinned card on page load).
 *
 * @param {*} _form
 * @returns {Object<string,string>}
 */
export const checkboxFilterDefaults = _form => {
	const form = toFormEl( _form );
	const defaults = {};
	if ( ! form ) return defaults;

	form.querySelectorAll( CHECKBOX_SELECTOR ).forEach( el => {
		if ( ! el.name ) return;
		defaults[ el.name ] = el.getAttribute( 'data-fr-default' ) || CHECKBOX_OFF;
	} );

	return defaults;
};

/**
 * Put every checkbox filter back to its declared default (used by Clear filters).
 *
 * @param {*} _form
 */
export const resetCheckboxFilters = _form => {
	const form = toFormEl( _form );
	if ( ! form ) return;

	form.querySelectorAll( CHECKBOX_SELECTOR ).forEach( el => {
		el.checked = ( el.getAttribute( 'data-fr-default' ) || CHECKBOX_OFF ) === el.value;
	} );
};

/**
 * Show/hide the element(s) a checkbox filter drives (`data-fr-toggles="<selector>"`),
 * scoped to the module root. Config-driven so this stays an engine feature rather
 * than a special case for one index (the news critical-alerts band uses it).
 *
 * @param {*} _filters vanilla-jq set for the filter form.
 * @param {*} _scope   vanilla-jq set to search for the targets (the module root).
 */
export const applyCheckboxToggles = ( _filters, _scope ) => {
	const form = toFormEl( _filters );
	const scope = _scope && _scope.el && _scope.el[ 0 ] ? _scope.el[ 0 ] : document;
	if ( ! form ) return;

	form.querySelectorAll( `${ CHECKBOX_SELECTOR }[data-fr-toggles]` ).forEach( el => {
		const selector = el.getAttribute( 'data-fr-toggles' );
		if ( ! selector ) return;

		scope.querySelectorAll( selector ).forEach( target => {
			//`d-none` (not a bespoke state class) to match how the engine already
			//toggles the pinned card + the conditional fieldsets.
			target.classList.toggle( 'd-none', ! el.checked );
		} );
	} );
};

/**
 * Set one named control's value, but ONLY to a value it actually offers (an existing
 * radio / <option>) — the design does the same with its `validCats` / `validSorts`
 * whitelists, so a stale or hand-typed deep link can't put the filters in a state
 * the query can't honour.
 *
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {string} value
 * @returns {boolean} whether anything changed.
 */
const setControlValue = ( form, name, value ) => {
	const controls = Array.from( form.elements ).filter( el => el.name === name );
	if ( ! controls.length ) return false;

	const first = controls[ 0 ];

	if ( first.type === 'radio' ) {
		const match = controls.find( el => el.value === value );
		if ( ! match || match.checked ) return false;
		match.checked = true;
		return true;
	}

	if ( first.type === 'checkbox' ) {
		const checked = value === first.value;
		if ( ! checked && value !== CHECKBOX_OFF ) return false; //not a value it offers
		if ( first.checked === checked ) return false;
		first.checked = checked;
		return true;
	}

	if ( first.tagName === 'SELECT' ) {
		const match = Array.from( first.options ).find( o => o.value === value );
		if ( ! match || first.value === value ) return false;
		first.value = value;
		return true;
	}

	if ( first.value === value ) return false;
	first.value = value;
	return true;
};

/**
 * Initialise the filters from `#hash` params (`#cat=…`, `#sort=…`, or the full
 * `#resource-category=…`). The QUERY STRING always wins — the server-side initial
 * render already used it, so re-applying the hash over it would fight that render.
 *
 * @param {*} _filters vanilla-jq set for the filter form.
 * @returns {boolean} whether any control changed (→ the caller re-runs the query).
 */
export const applyHashFilters = _filters => {
	const form = toFormEl( _filters );
	if ( ! form ) return false;

	const raw = ( window.location.hash || '' ).replace( /^#/, '' );
	if ( ! raw || raw.indexOf( '=' ) === -1 ) return false;

	const hash = new URLSearchParams( raw );
	const query = new URLSearchParams( window.location.search );
	let applied = false;

	const names = [
		...new Set(
			Array.from( form.elements )
				.filter( el => el.name && el.type !== 'hidden' && el.name !== 'p_type' )
				.map( el => el.name )
		),
	];

	names.forEach( name => {
		const keys = [ name, ...HASH_ALIASES.filter( a => a.test( name ) ).map( a => a.alias ) ];

		//`?query` beats `#hash` for the same filter (mirrors the design's getParam).
		if ( keys.some( k => query.has( k ) ) ) return;

		const key = keys.find( k => hash.has( k ) );
		if ( ! key ) return;

		if ( setControlValue( form, name, hash.get( key ) ) ) applied = true;
	} );

	return applied;
};
